import { useServiceHistoryInitMutation, useVehicleLookupQuery } from '../../api';
import { LookupField } from './lookup-field';
import {
  CDKVehicleHistorySection,
  ProofOfServiceHistorySection,
  VauxhallVehicleHistoryPDFSection,
  VauxhallVehicleHistorySection
} from './sections';
import styles from './styles.module.scss';
import { ServiceHistoryModalProps, SectionTab, TABS } from './types';
import { VehicleInfo } from './vehicle-info';
import { ServiceHistoryModel } from 'api-new/generated';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

type ModalContentProps = Omit<ServiceHistoryModalProps, 'onClose'> & {
  onClose: () => void;
};

export const ModalContent: FC<ModalContentProps> = ({ regNo: initialRegNo, vin, onClose }) => {
  const [currentTab, setCurrentTab] = useState<SectionTab>('CDK Vehicle History');
  const [searchRegNo, setSearchRegNo] = useState(initialRegNo);
  const [serviceHistoryData, setServiceHistoryData] = useState<ServiceHistoryModel | null>(null);

  const { data: vehicleData } = useVehicleLookupQuery({ regNo: searchRegNo }, { enabled: !!searchRegNo });

  const { mutate: initServiceHistory, isLoading: isLoadingInit } = useServiceHistoryInitMutation();

  useEffect(() => {
    if (!searchRegNo) return;

    initServiceHistory(
      { regNo: searchRegNo, vin },
      {
        onSuccess: (data) => setServiceHistoryData(data)
      }
    );
  }, [searchRegNo, vin, initServiceHistory]);

  const handleLookup = useCallback(
    (newRegNo: string) => {
      if (newRegNo.toLowerCase() !== searchRegNo?.toLowerCase()) {
        setSearchRegNo(newRegNo);
      } else {
        initServiceHistory(
          { regNo: newRegNo, vin },
          {
            onSuccess: (data) => setServiceHistoryData(data)
          }
        );
      }
    },
    [searchRegNo, vin, initServiceHistory]
  );

  const vehicleInfo = vehicleData?.dataItems?.vehicleRegistration?.makeModel;

  const renderTabContent = useMemo(() => {
    const serviceHistoryId = serviceHistoryData?.id;

    switch (currentTab) {
      case 'CDK Vehicle History':
        return <CDKVehicleHistorySection serviceHistoryId={serviceHistoryId} />;
      case 'Vauxhall Vehicle History':
        return <VauxhallVehicleHistorySection serviceHistoryId={serviceHistoryId} />;
      case 'Proof of Service History':
        return <ProofOfServiceHistorySection serviceHistoryId={serviceHistoryId} />;
      case 'Vauxhall Vehicle History PDF':
        return <VauxhallVehicleHistoryPDFSection regNo={searchRegNo} />;
      default:
        return null;
    }
  }, [currentTab, serviceHistoryData?.id, searchRegNo]);

  return (
    <div className={styles.container}>
      <div className="row">
        <div className="col-12 col-md-4 mt-10">
          <LookupField regNo={initialRegNo} onLookup={handleLookup} isLoading={isLoadingInit} />
        </div>
        <div className="col-12 col-md-6 mt-4 mt-md-10 d-flex align-items-center">
          <VehicleInfo vehicleInfo={vehicleInfo} />
        </div>
      </div>

      <div className="row">
        <div className="col-12 mt-6">
          <ul className={`${styles.tabContainer} nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent`}>
            {TABS.map((item) => (
              <li key={item.tab} className="nav-item">
                <a
                  className={`nav-link text-active-secondary mx-0${currentTab === item.tab ? ' active' : ''}`}
                  onClick={() => setCurrentTab(item.tab)}
                >
                  {item.tab}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="row">
        <div className="col-12">{renderTabContent}</div>
      </div>

      <div className="row mt-10">
        <div className="col-12">
          <button type="button" onClick={onClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
